/**
 * Offline caching, started on sign-in and filled one stage at a time.
 *
 * Two things used to happen on first paint, for every visitor: the service
 * worker registered itself (injected by vite-plugin-pwa), and its install
 * event fetched the entire precache manifest — every route chunk and every
 * local image, ~280 files, in one burst. Nobody had asked for any of it.
 *
 * Now nothing is registered until there is a session, the precache is the
 * shell alone, and route code is pulled in priority order while the browser
 * is idle. See `workbox` in vite.config.ts for the other half.
 */

/** One step of the warm-up. Stages run in array order, never concurrently. */
type Stage = {
  id: string;
  /**
   * Pulls the stage's chunks in. These are the same dynamic imports the
   * router uses, so Rollup gives them the same chunk and a later navigation
   * resolves the module that is already here rather than fetching again.
   */
  load: () => Promise<unknown>;
};

const STAGES: Stage[] = [
  // Home first: it is where sign-in lands, so this one is usually already
  // in flight. Listing it costs nothing and guarantees the order.
  { id: 'home', load: () => import('@/pages/Home') },

  // Money second. These two are the pages a session actually came here for,
  // and the ones where a spinner is most expensive.
  {
    id: 'wallet',
    load: () =>
      Promise.all([
        import('@/pages/Wallet/Deposit'),
        import('@/pages/Wallet/Withdraw'),
        import('@/pages/Wallet/Record'),
      ]),
  },

  // Games third, with Play included — it is the heaviest chunk in the app
  // and the one most likely to be opened over a bad connection.
  {
    id: 'game',
    load: () =>
      Promise.all([
        import('@/pages/Game'),
        import('@/pages/Game/List'),
        import('@/pages/Game/Play'),
      ]),
  },

  // Everything else, last and lowest value.
  {
    id: 'rest',
    load: () =>
      Promise.all([
        import('@/pages/Mine'),
        import('@/pages/Activity'),
        import('@/pages/Mine/Settings'),
      ]),
  },
];

/** Registration and warming both happen once per page load, not per call. */
let started = false;

/**
 * Runs `fn` when the browser has nothing better to do.
 *
 * The timeout is the point: without it a busy tab can starve the callback
 * indefinitely, and the warm-up would simply never happen on exactly the
 * slow devices it is meant to help. Safari has no `requestIdleCallback`, so
 * it gets a timer.
 */
function onIdle(fn: () => void, timeout = 10_000): void {
  const ric = (window as unknown as {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void;
  }).requestIdleCallback;
  if (ric) ric(fn, { timeout });
  else window.setTimeout(fn, 1_200);
}

/**
 * Whether it is reasonable to spend the user's data on this.
 *
 * Data Saver is an explicit request not to; 2g means the warm-up would
 * compete with the page the user is actually looking at.
 */
function shouldWarm(): boolean {
  const conn = (navigator as unknown as {
    connection?: { saveData?: boolean; effectiveType?: string };
  }).connection;
  if (!conn) return true;
  if (conn.saveData) return false;
  return conn.effectiveType !== 'slow-2g' && conn.effectiveType !== '2g';
}

async function warmStages(): Promise<void> {
  if (!shouldWarm()) return;

  for (const stage of STAGES) {
    // Wait for an idle moment before *each* stage, not once up front: the
    // user is browsing while this runs, and a stage that started during a
    // navigation would compete with the chunk they are waiting on.
    await new Promise<void>((resolve) => onIdle(resolve));
    try {
      await stage.load();
    } catch {
      // A chunk that fails to load is the router's problem to report when
      // the user actually goes there. Warming is best-effort by definition,
      // and one bad stage must not stop the rest.
    }
  }
}

/**
 * Turn on offline caching for a signed-in session.
 *
 * Safe to call repeatedly — every auth path funnels through `setSession`,
 * including session refreshes, so this runs on each of them and does its
 * work only the first time.
 */
export function enableOfflineCache(): void {
  if (started) return;
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
  started = true;

  void (async () => {
    try {
      // Imported dynamically so the registration code stays out of the
      // entry chunk, which every visitor downloads whether or not they ever
      // sign in.
      const { registerSW } = await import('virtual:pwa-register');
      registerSW({
        immediate: true,
        // A new worker only activates when applyUpdate (./appUpdate.ts) tells
        // it to, and applyUpdate reloads the page itself. Without this the
        // plugin's own reload-on-activate would fire too: a double reload.
        onNeedReload: () => {},
      });
    } catch {
      // No worker (unsupported browser, insecure origin, dev without the
      // PWA plugin). The app works without one; only the caching is lost.
      return;
    }
    void warmStages();
  })();
}
